import { Loader2, ShieldOff } from 'lucide-react';
import type { ExcludedAlertState } from '@kirin/api-client/contracts/alerts';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { createMutationHandler } from '@/hooks/mutationHelpers';
import { useOrgWriteGate } from '@/hooks/useRoleCheck';
import { useToast } from '@/hooks/use-toast';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/lib/analytics';
import { formatExcludedTooltipLines } from '@/lib/excludedAlertFormatting';
import {
  useRemoveAlertExclusion,
  type AlertExcludeScope,
} from '@/hooks/api/useAlertExclusions';
import { EXCLUDE_BUTTON_WIDTH_CLASS } from '@/components/AlertExcludeButton';
import { SUPPORT_READONLY_TOOLTIP } from '@/components/alertActionTooltipCopy';

/**
 * Props for {@link AlertRemoveExclusionButton}.
 */
export interface AlertRemoveExclusionButtonProps {
  /** Scope of the alert (org-shared or self/account). */
  scope: AlertExcludeScope;
  /** The alert key whose exclusion to remove. */
  alertKey: string;
  /** Optional className passed to the trigger Button. */
  className?: string;
  /** Optional callback invoked after a successful removal. */
  onRemoved?: () => void;
  /**
   * Optional exclude fragment from the grouped alert. When provided and the
   * alert is currently excluded, the trigger renders inside a tooltip
   * describing who excluded it, when, and until when. Passing
   * `null`/`undefined` (or an already-cleared state) keeps the bare button —
   * the tooltip is purely additive metadata, never required for the action
   * itself. Mirrors `AlertUnmuteButton`'s `mute` prop exactly.
   */
  exclude?: ExcludedAlertState | null;
  /**
   * Optional analytics dimensions threaded into the `alert_exclusion_removed`
   * Mixpanel event. See {@link AlertExcludeButton} for the rationale.
   */
  analyticsContext?: {
    severity?: string;
    type?: string;
  };
  /**
   * KIR-4700: renders the trigger as an icon-only square button (no visible
   * "Remove Exclusion" label) for the condensed alerts-table Actions column.
   * The tooltip still carries the label/details.
   */
  iconOnly?: boolean;
}

/**
 * One-click control to remove an existing exclusion from an alert
 * (KIR-2363 + KIR-2447).
 *
 * Identical gating to {@link AlertExcludeButton}: for org-scope, gated by
 * `useIsOwnerOrManager` so members cannot remove org-shared exclusions.
 *
 * Wraps `mutateAsync` with the shared {@link createMutationHandler} so
 * users see a consistent success/error toast across every consumer surface.
 *
 * Emits `alert_exclusion_removed` after a confirmed mutation success with
 * the same property shape as `alert_unmuted` (scope, alert_key, severity,
 * type). The analytics call is wrapped in its own try-block isolated from
 * `onRemoved` so a misbehaving consumer cannot suppress the event.
 */
export function AlertRemoveExclusionButton({
  scope,
  alertKey,
  className,
  onRemoved,
  exclude,
  analyticsContext,
  iconOnly,
}: AlertRemoveExclusionButtonProps): React.ReactElement | null {
  const writeGate = useOrgWriteGate(
    scope.kind === 'organization' ? scope.organizationId : undefined
  );

  const removeMutation = useRemoveAlertExclusion(scope);
  const { toast } = useToast();

  const handleRemove = createMutationHandler(removeMutation, toast, {
    successMessage: 'Exclusion removed',
    errorMessage: 'Failed to remove exclusion',
  });

  if (scope.kind === 'organization') {
    if (writeGate.isLoading) return null;
    if (writeGate.hidden) return null;
  }
  const writeDisabled = scope.kind === 'organization' && writeGate.disabled;

  const handleClick = async (): Promise<void> => {
    try {
      await handleRemove({ alertKey });
      // Both side effects below run only on a confirmed mutation success.
      // They are deliberately ISOLATED from each other: a throw in one must
      // never block the other (telemetry/UX divergence is much worse than a
      // single skipped event). Order: callback first (user-observable),
      // then analytics (background). Mirrors `AlertUnmuteButton` exactly.
      try {
        onRemoved?.();
      } catch {
        // Caller-supplied callbacks are out of our control. Swallowing here
        // ensures a misbehaving consumer can't suppress the analytics event
        // for a mutation the backend has already accepted.
      }
      trackEvent('alert_exclusion_removed', {
        scope: scope.kind,
        alert_key: alertKey,
        severity: analyticsContext?.severity,
        type: analyticsContext?.type,
      });
    } catch {
      // createMutationHandler already toasted; swallow so the click handler
      // does not bubble an unhandled rejection up to React's error boundary.
    }
  };

  const isPending = removeMutation.isPending;

  const button = (
    <Button
      type="button"
      variant="outline"
      size={iconOnly ? 'icon-sm' : 'sm'}
      className={cn(!iconOnly && EXCLUDE_BUTTON_WIDTH_CLASS, className)}
      disabled={isPending || writeDisabled}
      aria-label="Remove exclusion"
      onClick={() => void handleClick()}
    >
      {isPending ? (
        <Loader2 className="size-4 animate-spin" />
      ) : (
        <ShieldOff className="size-4" />
      )}
      {!iconOnly && <span>Remove Exclusion</span>}
    </Button>
  );

  if (writeDisabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>{SUPPORT_READONLY_TOOLTIP}</TooltipContent>
      </Tooltip>
    );
  }

  if (!exclude?.excluded) {
    return button;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent>
        <ExcludedTooltipLines exclude={exclude} />
      </TooltipContent>
    </Tooltip>
  );
}

AlertRemoveExclusionButton.displayName = 'AlertRemoveExclusionButton';

/**
 * Mounted only while the tooltip is open, so a display-zone change re-renders
 * the open tooltip rather than every excluded row's button.
 */
function ExcludedTooltipLines({
  exclude,
}: {
  exclude: ExcludedAlertState;
}): React.ReactElement | null {
  const timeZone = useDisplayTimeZone();
  const lines = formatExcludedTooltipLines(exclude, timeZone);
  if (!lines) return null;
  return (
    <div className="flex flex-col gap-0.5 text-left">
      <span>Excluded by {lines.excluder}</span>
      <span>On {lines.excludedAt}</span>
      <span>Until {lines.excludedUntil}</span>
    </div>
  );
}
